/**
 * viewerStore
 *
 * Holds:
 *   - the currently-loaded sheet id
 *   - a (non-serialised) reference to the live Autodesk.Viewing.GuiViewer3D
 *   - a `ready` flag that flips true once the viewer has finished loading the
 *     current document
 *
 * Pattern matches Tyto's `viewerStateStore.ts` for everything serialisable;
 * the live viewer instance is intentionally kept out of any subscriber that
 * would re-render unnecessarily.
 */
import { create } from "zustand";
import { DEFAULT_PROJECT_ID, getProject } from "../data/projects";

interface ViewerStore {
  currentSheetId: string;
  viewer: Autodesk.Viewing.GuiViewer3D | null;
  /** True once the current sheet's document has finished loading. */
  ready: boolean;

  setCurrentSheet: (id: string) => void;
  setViewer: (v: Autodesk.Viewing.GuiViewer3D | null) => void;
  setReady: (r: boolean) => void;
}

export const useViewerStore = create<ViewerStore>(set => ({
  // Initialise with the boot project's default sheet. App.tsx /
  // FormaApp.tsx re-invoke setCurrentSheet when the user switches
  // projects (each project has its own sheet set).
  currentSheetId: getProject(DEFAULT_PROJECT_ID).defaultSheetId,
  viewer: null,
  ready: false,

  setCurrentSheet: id =>
    set(state =>
      state.currentSheetId === id ? state : { currentSheetId: id, ready: false }
    ),
  setViewer: viewer => set({ viewer }),
  setReady: ready => set({ ready }),
}));
